import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * A custom element that allows the user to select a quantity.
 *
 * This component follows a pure event-driven architecture where quantity changes
 * are broadcast via QuantitySelectorUpdateEvent. Parent components that contain
 * quantity selectors listen for these events and handle them according to their
 * specific needs, with event filtering ensuring each parent only processes events
 * from its own quantity selectors to prevent conflicts between different cart
 * update strategies.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;
  isGridMode = false;
  #pendingCartUpdate = null; // Track pending API call
  #syncDebounceTimer = null; // Debounce timer for external sync
  #lastKnownCartQty = 0; // Track last known cart quantity (from server)
  #updateDebounceTimer = null; // Debounce timer for user clicks
  #optimisticQty = 0; // Current optimistic/displayed quantity

  connectedCallback() {
    super.connectedCallback();

    // Check if we're in grid mode
    const { quantityInput } = this.refs;
    this.isGridMode = quantityInput?.hasAttribute('data-grid-mode') || false;

    // Capture server-disabled state on first load
    const { minusButton, plusButton } = this.refs;

    if (minusButton.disabled) {
      this.serverDisabledMinus = true;
    }
    if (plusButton.disabled) {
      this.serverDisabledPlus = true;
    }

    this.initialized = true;
    
    // In grid mode, sync with cart on load and listen for updates
    if (this.isGridMode) {
      // Initialize optimistic quantity from input value (set by Liquid from cart)
      const initialQty = parseInt(quantityInput.value) || 0;
      this.#optimisticQty = initialQty;
      this.#lastKnownCartQty = initialQty;
    }
    
    // Update button states AFTER setting optimistic quantity
    this.updateButtonStates();
    
    if (this.isGridMode) {
      this.syncWithCart();
      document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdateEvent);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Remove event listener in grid mode
    if (this.isGridMode) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdateEvent);
    }
    
    // Cancel any pending updates
    if (this.#pendingCartUpdate) {
      this.#pendingCartUpdate.abort();
      this.#pendingCartUpdate = null;
    }
    
    // Clear debounce timers
    if (this.#syncDebounceTimer) {
      clearTimeout(this.#syncDebounceTimer);
      this.#syncDebounceTimer = null;
    }
    
    if (this.#updateDebounceTimer) {
      clearTimeout(this.#updateDebounceTimer);
      this.#updateDebounceTimer = null;
    }
  }
  
  /**
   * Handles cart update events from other components
   * @param {CartUpdateEvent} event
   */
  handleCartUpdateEvent = (event) => {
    if (!this.isGridMode) return;
    
    const eventVariantId = event.detail?.data?.variantId;
    
    // Skip if this update came from this component
    if (event.detail?.sourceId === this.id) {
      return;
    }
    
    // Skip if this update is for a different variant
    if (eventVariantId && eventVariantId.toString() !== this.dataset.variantId) {
      return;
    }
    
    // Debounce sync to avoid multiple rapid updates
    if (this.#syncDebounceTimer) {
      clearTimeout(this.#syncDebounceTimer);
    }
    
    this.#syncDebounceTimer = setTimeout(() => {
      this.syncWithCart();
      this.#syncDebounceTimer = null;
    }, 150); // 150ms debounce
  };

  /**
   * Updates cart quantity and refreshes component state
   * @param {number} cartQty - The quantity currently in cart for this variant
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    
    // In grid mode, update the input value to show cart quantity
    if (this.isGridMode) {
      // CRITICAL: Don't overwrite optimistic updates
      // Only update if we don't have a pending update or debounce timer
      if (!this.#pendingCartUpdate && !this.#updateDebounceTimer) {
        this.refs.quantityInput.value = cartQty.toString();
        this.#optimisticQty = cartQty;
        this.updateButtonStates();
      }
      return;
    }
    
    // Product page mode: update effective max
    this.updateCartQuantity();
  }

  /**
   * Checks if the current quantity can be added to cart without exceeding max
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}} Validation result
   */
  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();
    const quantityToAdd = value;
    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  /**
   * Gets the current quantity value
   * @returns {string} The current value
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * Sets the current quantity value
   * @param {string} value - The value to set
   */
  setValue(value) {
    this.refs.quantityInput.value = value;
  }

  /**
   * Updates min/max/step constraints and snaps value to valid increment
   * @param {string} min - Minimum value
   * @param {string|null} max - Maximum value (null if no max)
   * @param {string} step - Step increment
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value) || 0;

    quantityInput.min = min;
    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }
    quantityInput.step = step;

    const newMin = parseIntOrDefault(min, 1);
    const newStep = parseIntOrDefault(step, 1);
    const effectiveMax = this.getEffectiveMax();

    // Snap to valid increment if not already aligned
    let newValue = currentValue;
    if ((currentValue - newMin) % newStep !== 0) {
      // Snap DOWN to closest valid increment
      newValue = newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
    }

    // Ensure value is within bounds
    newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));

    if (newValue !== currentValue) {
      quantityInput.value = newValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets current values from DOM (fresh read every time)
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(quantityInput.min, 1),
      max: parseIntOrDefault(quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
    };
  }

  /**
   * Gets the effective maximum value for this quantity selector
   * Product page: max - cartQuantity (how many can be added)
   * Grid mode: absolute max (total cart quantity)
   * Override in subclass for different behavior
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max, cartQuantity, min } = this.getCurrentValues();
    if (max === null) return null;
    
    // Grid mode: use absolute max
    if (this.isGridMode) {
      return max;
    }
    
    // Product page: can only add what's left
    return Math.max(max - cartQuantity, min);
  }

  /**
   * Disable or enable both buttons (used during API calls)
   * @param {boolean} disabled - Whether to disable the buttons
   */
  setButtonsDisabled(disabled) {
    const { minusButton, plusButton } = this.refs;
    
    if (!this.serverDisabledMinus) {
      minusButton.disabled = disabled;
    }
    
    if (!this.serverDisabledPlus) {
      plusButton.disabled = disabled;
    }
    
    // If re-enabling, update to correct states
    if (!disabled) {
      this.updateButtonStates();
    }
  }

  /**
   * Returns the effective minimum quantity for a grid-mode selector.
   *
   * In grid mode the HTML `min` attribute stays at 0 (so the input represents
   * the literal cart quantity, which can legitimately be zero), but the product
   * still has a real minimum-orderable quantity once it enters the cart — e.g.
   * box-only wines with `box_contents = 6`, or B2B variants with
   * `quantity_rule.min = 12`. That real minimum is mirrored onto the input via
   * `data-effective-min` by `snippets/quantity-selector.liquid`.
   *
   * @returns {number}
   */
  #getGridModeEffectiveMin() {
    const raw = this.refs.quantityInput.getAttribute('data-effective-min');
    const parsed = parseIntOrDefault(raw, 1);
    return parsed > 0 ? parsed : 1;
  }

  /**
   * Computes the delta a + click should apply in grid mode.
   *
   * - From zero: jump to `max(effectiveMin, step)` so the customer lands on a
   *   legal order quantity for box-only / B2B-restricted products.
   * - Above zero: step by `step` (which is `box_contents` for box-only
   *   products, or `quantity_rule.increment` for B2B / NN Pricing).
   *
   * @returns {number}
   */
  #computeGridIncreaseDelta() {
    const { step } = this.getCurrentValues();
    const safeStep = step > 0 ? step : 1;
    if (this.#optimisticQty === 0) {
      return Math.max(this.#getGridModeEffectiveMin(), safeStep);
    }
    return safeStep;
  }

  /**
   * Computes the delta a − click should apply in grid mode.
   *
   * Steps down by `step` normally, but if a single step would take the
   * customer below the effective minimum without reaching zero (e.g. starting
   * from 12 with min=12, step=6 — going to 6 is below the legal floor of 12),
   * remove the line entirely instead.
   *
   * @returns {number}
   */
  #computeGridDecreaseDelta() {
    const { step } = this.getCurrentValues();
    const safeStep = step > 0 ? step : 1;
    const effMin = this.#getGridModeEffectiveMin();
    const tentative = this.#optimisticQty - safeStep;
    if (tentative > 0 && tentative < effMin) {
      return -this.#optimisticQty;
    }
    return -safeStep;
  }

  /**
   * Updates button states based on current value and limits
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Only manage buttons that weren't server-disabled
    if (!this.serverDisabledMinus) {
      // In grid mode, use optimistic quantity
      if (this.isGridMode) {
        minusButton.disabled = this.#optimisticQty <= 0;
      } else {
        minusButton.disabled = value <= min;
      }
    }

    if (!this.serverDisabledPlus) {
      // In grid mode, check against optimistic quantity + effective max
      if (this.isGridMode) {
        if (effectiveMax === null) {
          plusButton.disabled = false;
        } else {
          // Look ahead by the actual delta the next + click would apply so we
          // disable when a full step (or the initial min) wouldn't fit, not
          // just when the current value equals the max.
          const nextDelta = this.#computeGridIncreaseDelta();
          plusButton.disabled = this.#optimisticQty + nextDelta > effectiveMax;
        }
      } else {
        plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
      }
    }
    
    // In grid mode, update data-has-quantity attribute for styling
    if (this.isGridMode) {
      this.setAttribute('data-has-quantity', this.#optimisticQty > 0 ? 'true' : 'false');
    }
  }

  /**
   * Updates quantity by a given step
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * stepMultiplier));

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    
    // In grid mode, add to cart immediately
    if (this.isGridMode) {
      this.addToCartInGridMode(this.#computeGridIncreaseDelta());
    } else {
      this.updateQuantity(1);
    }
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    
    // In grid mode, remove from cart immediately
    if (this.isGridMode) {
      this.addToCartInGridMode(this.#computeGridDecreaseDelta());
    } else {
      this.updateQuantity(-1);
    }
  }
  
  /**
   * Adds or removes items from cart in grid mode
   * Debounces rapid clicks and batches them into a single API call
   * @param {number} delta - Positive to add, negative to remove.
   *   In grid mode this is the *actual* quantity change (typically `step` or
   *   `max(min, step)` from zero, computed by the +/- handlers above) rather
   *   than a step multiplier. Box-only / B2B-restricted products use this to
   *   move in multiples of `box_contents` or `quantity_rule.increment`.
   */
  addToCartInGridMode(delta) {
    const { quantityInput } = this.refs;
    const variantId = this.dataset.variantId;
    
    if (!variantId) {
      console.error('No variantId found!');
      return;
    }
    
    // Update optimistic quantity, clamping to [0, effectiveMax] so a + click
    // that would overshoot the max (e.g. step=6, current=6, max=10) snaps to
    // the max rather than going over.
    const effectiveMax = this.getEffectiveMax();
    let newQty = this.#optimisticQty + delta;
    if (newQty < 0) newQty = 0;
    if (effectiveMax !== null && newQty > effectiveMax) newQty = effectiveMax;
    this.#optimisticQty = newQty;
    
    // 1. INSTANT UI UPDATE - always immediate
    quantityInput.value = this.#optimisticQty.toString();
    this.updateButtonStates();
    
    // 2. DEBOUNCE API CALL - wait for user to stop clicking
    if (this.#updateDebounceTimer) {
      clearTimeout(this.#updateDebounceTimer);
    }
    
    this.#updateDebounceTimer = setTimeout(() => {
      // Disable buttons during API call
      this.setButtonsDisabled(true);
      
      this.#performCartUpdate(variantId, this.#optimisticQty);
      this.#updateDebounceTimer = null;
    }, 500); // 500ms debounce
  }
  
  /**
   * Performs the actual cart update after debounce
   * @param {string} variantId
   * @param {number} targetQty
   */
  async #performCartUpdate(variantId, targetQty) {
    // Cancel any pending cart update
    if (this.#pendingCartUpdate) {
      this.#pendingCartUpdate.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    this.#pendingCartUpdate = abortController;
    
    try {
      // Use /cart/add.js when adding a new product (from 0), otherwise use /cart/change.js
      const isAddingNew = this.#lastKnownCartQty === 0 && targetQty > 0;
      const endpoint = isAddingNew ? '/cart/add.js' : '/cart/change.js';
      
      const body = isAddingNew
        ? { items: [{ id: variantId, quantity: targetQty }] }
        : { id: variantId, quantity: targetQty };
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal: abortController.signal,
      });
      
      if (!response.ok) {
        throw new Error('Failed to update cart');
      }
      
      let cart = await response.json();

      // /cart/add.js returns the added line item(s), not the cart, so it has no
      // item_count. Without it the CartUpdateEvent below carries
      // itemCount: undefined, the cart-icon reads it as 0 and hides the count
      // badge even though items were just added (LG-379). Fetch the real cart so
      // the header total stays correct. /cart/change.js already returns the cart.
      if (typeof cart.item_count !== 'number') {
        cart = await fetch('/cart.js', { signal: abortController.signal }).then((response) => response.json());
      }

      // Only process if this is still the active request
      if (this.#pendingCartUpdate === abortController) {
        this.#pendingCartUpdate = null;
        
        // Update both last known and optimistic to match server
        this.#lastKnownCartQty = targetQty;
        this.#optimisticQty = targetQty;
        
        // Update data-cart-quantity attribute
        this.refs.quantityInput.setAttribute('data-cart-quantity', targetQty.toString());
        
        // Re-enable buttons after successful update
        this.setButtonsDisabled(false);
        
        // Dispatch CartUpdateEvent
        const cartUpdateEvent = new CartUpdateEvent(
          cart,
          this.id || 'quantity-selector-grid',
          {
            itemCount: cart.item_count,
            variantId: variantId,
            source: 'quantity-selector-grid',
          }
        );
        document.dispatchEvent(cartUpdateEvent);
      }
    } catch (error) {
      // Ignore abort errors (expected when cancelling)
      if (error.name === 'AbortError') return;
      
      console.error('Failed to update cart in grid mode:', error);
      
      // Only rollback if this is still the active request
      if (this.#pendingCartUpdate === abortController) {
        this.#pendingCartUpdate = null;
        // Rollback to last known good quantity
        this.#optimisticQty = this.#lastKnownCartQty;
        this.refs.quantityInput.value = this.#lastKnownCartQty.toString();
        this.refs.quantityInput.setAttribute('data-cart-quantity', this.#lastKnownCartQty.toString());
        
        // Re-enable buttons after error
        this.setButtonsDisabled(false);
        this.updateButtonStates();
      }
    }
  }
  
  /**
   * Syncs quantity display with cart
   * Only updates if value actually changed to avoid unnecessary re-renders
   */
  async syncWithCart() {
    const variantId = this.dataset.variantId;
    if (!variantId) return;
    
    // CRITICAL: Don't sync if we have a pending update OR debounced update
    if (this.#pendingCartUpdate || this.#updateDebounceTimer) {
      return;
    }
    
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const cartItem = cart.items.find(item => item.variant_id.toString() === variantId);
      const cartQty = cartItem ? cartItem.quantity : 0;
      
      // Only update if the value actually changed
      if (cartQty !== this.#lastKnownCartQty) {
        this.#lastKnownCartQty = cartQty;
        this.#optimisticQty = cartQty;
        this.setCartQuantity(cartQty);
      }
    } catch (error) {
      console.error('Failed to sync with cart:', error);
    }
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event (on blur).
   * Validates and snaps to valid values.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (this.isGridMode) {
      this.#commitGridModeQuantity(parseInt(event.target.value, 10));
      return;
    }

    // Snap to bounds
    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, parseInt(event.target.value) || 0));

    // Validate step increment
    if ((quantity - min) % step !== 0) {
      // Set the invalid value and trigger native HTML validation
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Keyboard handler for the quantity input.
   *
   * The input is editable in grid mode (the customer can type a target
   * quantity directly instead of clicking ± buttons), but `<input
   * type="number">` doesn't fire `blur` on Enter and we're not inside a form,
   * so we wire Enter → blur explicitly. Escape reverts the typed value back
   * to the last-known cart quantity without committing.
   *
   * @param {KeyboardEvent} event
   */
  onQuantityKeyDown(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      event.target.blur();
      return;
    }

    if (event.key === 'Escape' && this.isGridMode) {
      event.preventDefault();
      event.target.value = this.#optimisticQty.toString();
      event.target.blur();
    }
  }

  /**
   * Commits a manually-typed quantity in grid mode.
   *
   * Snaps the typed value to the nearest legal quantity for the variant —
   * respecting `effective_qty_min` (the smallest legal cart line) and `step`
   * (`box_contents` for box-only products, `quantity_rule.increment` for
   * B2B / NN Pricing). Values clearly under the floor collapse to 0 ("remove
   * from cart"); other under-min values snap up to the floor; over-max values
   * snap down to the largest step-aligned quantity that fits.
   *
   * After snapping, the result is applied via {@link addToCartInGridMode} so
   * the optimistic UI + debounced API call path stays identical to the
   * one used by the ± buttons.
   *
   * @param {number} typedRaw - The number parsed from the input.
   */
  #commitGridModeQuantity(typedRaw) {
    const { quantityInput } = this.refs;
    const { step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const safeStep = step > 0 ? step : 1;
    const effMin = this.#getGridModeEffectiveMin();
    const typed = Math.max(0, Number.isFinite(typedRaw) ? typedRaw : 0);

    let target;
    if (typed === 0) {
      target = 0;
    } else if (typed < effMin) {
      // Below floor: very small values look like "remove"; otherwise bump up
      // to the legal minimum so the cart line ends up valid.
      target = typed * 2 <= effMin ? 0 : effMin;
    } else {
      // Snap to the step-aligned grid anchored at the effective minimum.
      const offset = typed - effMin;
      target = effMin + Math.round(offset / safeStep) * safeStep;
    }

    if (effectiveMax !== null && target > effectiveMax) {
      // Snap down to the largest step-aligned value that still fits.
      const headroom = effectiveMax - effMin;
      target = effMin + Math.max(0, Math.floor(headroom / safeStep) * safeStep);
    }

    const delta = target - this.#optimisticQty;
    if (delta !== 0) {
      this.addToCartInGridMode(delta);
    } else {
      // No change in cart quantity, but the typed value may have been an
      // off-grid number — re-sync the visible input with the optimistic value
      // so the customer sees the snapped result.
      quantityInput.value = this.#optimisticQty.toString();
    }
  }

  /**
   * Handles the quantity change event.
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value);

    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
  }

  /**
   * Updates the cart quantity from data attribute and refreshes button states
   * Called when cart is updated from external sources
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { min, value, cartQuantity } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // In grid mode, keep value at cart quantity
    if (this.isGridMode) {
      quantityInput.value = cartQuantity.toString();
      this.updateButtonStates();
      return;
    }

    // Clamp value to new effective max if necessary
    const clampedValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value));

    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
